<template>
  <HrPageLayout
    title="Attendance Summary"
    subtitle="Monthly attendance overview and performance"
    icon="📊"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/employee-dashboard' },
      { label: 'Summary' }
    ]"
  >
    <!-- Loading State -->
    <div v-if="loading" class="hr-loading-wrap">
      <div class="hr-spinner"></div>
      <p>Loading attendance summary...</p>
    </div>

    <div v-else-if="summaryData" class="summary-content">
      <!-- Month Selector & Performance Score -->
      <div class="hr-content-grid hr-mb-4">
        <div class="hr-chart-card">
          <div class="hr-chart-body">
            <label class="hr-form-label">Select Month</label>
            <input
              v-model="selectedMonth"
              type="month"
              class="hr-input"
              @change="loadSummary"
              :max="maxMonth">
          </div>
        </div>
        <div class="hr-chart-card hr-bg-primary hr-text-white">
          <div class="hr-chart-body">
            <h6 class="hr-opacity-75 hr-mb-2">Performance Score</h6>
            <div class="hr-flex hr-align-center hr-gap-3">
              <h1 class="hr-mb-0 hr-fs-2">{{ summaryData.overview.performanceScore }}%</h1>
              <div class="hr-flex-1">
                <div class="hr-progress-wrap" style="height: 8px;">
                  <div
                    class="hr-progress-fill hr-bg-white hr-opacity-90"
                    :style="{ width: summaryData.overview.performanceScore + '%' }">
                  </div>
                </div>
                <small class="hr-opacity-75 hr-mt-1 hr-d-block">
                  {{ getPerformanceLabel(summaryData.overview.performanceScore) }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistics Cards -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-calendar-check"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Days Present</p>
          <h2 class="hr-metric-value">{{ summaryData.overview.daysPresent }}<span class="hr-text-muted hr-fs-sm">/ {{ summaryData.period.workingDays }}</span></h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Hours</p>
          <h2 class="hr-metric-value">{{ summaryData.overview.totalHours }}h</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Late Days</p>
          <h2 class="hr-metric-value">{{ summaryData.overview.lateDays }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-trending-up"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Overtime</p>
          <h2 class="hr-metric-value">{{ summaryData.overview.totalOvertime }}h</h2>
            </div>
        </div>
      </div>

      <!-- Attendance Rate & Location Breakdown -->
      <div class="hr-content-grid hr-mb-4">
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-pie-chart-alt hr-me-2"></i>Attendance Rate</h5>
          </div>
          <div class="hr-chart-body hr-text-center">
            <div class="hr-mb-4">
              <h1 class="hr-display-3 hr-text-primary hr-mb-0">{{ summaryData.overview.attendanceRate }}%</h1>
              <p class="hr-text-muted">{{ summaryData.overview.daysPresent }} of {{ summaryData.period.workingDays }} working days</p>
            </div>
            <div class="hr-flex hr-justify-around hr-border-top hr-pt-3">
              <div class="hr-p-3">
                <p class="hr-text-muted hr-mb-1">On Time</p>
                <h5 class="hr-text-success hr-mb-0">{{ summaryData.overview.onTimeDays }}</h5>
              </div>
              <div class="hr-p-3 hr-border-x">
                <p class="hr-text-muted hr-mb-1">Late</p>
                <h5 class="hr-text-warning hr-mb-0">{{ summaryData.overview.lateDays }}</h5>
              </div>
              <div class="hr-p-3">
                <p class="hr-text-muted hr-mb-1">Absent</p>
                <h5 class="hr-text-danger hr-mb-0">{{ summaryData.overview.absentDays }}</h5>
              </div>
            </div>
          </div>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-map-pin hr-me-2"></i>Location Breakdown</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="summaryData.locationBreakdown && summaryData.locationBreakdown.length > 0">
              <div v-for="(location, index) in summaryData.locationBreakdown" :key="index" class="hr-mb-3">
                <div class="hr-flex hr-justify-between hr-align-center hr-mb-2">
                  <div>
                    <span :class="getLocationBadgeClass(location.location_type)">
                      {{ location.location_type }}
                    </span>
                    <small class="hr-text-muted hr-ms-2">{{ location.unique_days }} days</small>
                  </div>
                  <strong>{{ location.count }} entries</strong>
                </div>
                <div class="hr-progress-wrap" style="height: 8px;">
                  <div
                    :class="getLocationProgressClass(location.location_type)"
                    :style="{ width: getLocationPercentage(location.count) + '%' }">
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="hr-text-center hr-text-muted hr-py-4">
              <i class="bx bx-map-pin bx-lg"></i>
              <p class="hr-mt-2">No location data available</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Punctuality Analysis -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-timer hr-me-2"></i>Punctuality Analysis</h5>
        </div>
        <div class="hr-chart-body">
          <div class="hr-flex hr-justify-around hr-flex-wrap hr-text-center">
            <div class="hr-p-3">
              <div class="hr-avatar-sm hr-mx-auto hr-mb-2 hr-bg-success">
                <i class="bx bx-check"></i>
              </div>
              <h4 class="hr-mb-1">{{ summaryData.punctuality.onTimeCount }}</h4>
              <p class="hr-text-muted hr-mb-0 hr-small">On Time</p>
            </div>
            <div class="hr-p-3">
              <div class="hr-avatar-sm hr-mx-auto hr-mb-2 hr-bg-warning">
                <i class="bx bx-time-five"></i>
              </div>
              <h4 class="hr-mb-1">{{ summaryData.punctuality.late1to15 }}</h4>
              <p class="hr-text-muted hr-mb-0 hr-small">1-15 min late</p>
            </div>
            <div class="hr-p-3">
              <div class="hr-avatar-sm hr-mx-auto hr-mb-2 hr-bg-orange">
                <i class="bx bx-alarm"></i>
              </div>
              <h4 class="hr-mb-1">{{ summaryData.punctuality.late16to30 }}</h4>
              <p class="hr-text-muted hr-mb-0 hr-small">16-30 min late</p>
            </div>
            <div class="hr-p-3">
              <div class="hr-avatar-sm hr-mx-auto hr-mb-2 hr-bg-danger">
                <i class="bx bx-error-circle"></i>
              </div>
              <h4 class="hr-mb-1">{{ summaryData.punctuality.lateOver30 }}</h4>
              <p class="hr-text-muted hr-mb-0 hr-small">Over 30 min late</p>
            </div>
          </div>
          <div class="hr-text-center hr-mt-3 hr-pt-3 hr-border-top">
            <p class="hr-text-muted hr-mb-0">
              Average Late Time: <strong>{{ summaryData.punctuality.avgLateMinutes }} minutes</strong>
            </p>
          </div>
        </div>
      </div>

      <!-- Weekly Breakdown & Top Late Days -->
      <div class="hr-content-grid hr-mb-4">
        <div class="hr-chart-card hr-span-2">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-calendar-week hr-me-2"></i>Weekly Breakdown</h5>
          </div>
          <div class="hr-chart-body">
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-sm">
                <thead>
                <tr>
                  <th>Week</th>
                  <th class="hr-text-center">Days</th>
                  <th class="hr-text-center">Hours</th>
                  <th class="hr-text-center">Late</th>
                  <th class="hr-text-center">Avg Hours/Day</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(week, index) in summaryData.weeklyBreakdown" :key="index">
                  <td>
                    <strong>Week {{ index + 1 }}</strong>
                    <br>
                    <small class="hr-text-muted">{{ formatDate(week.week_start) }} - {{ formatDate(week.week_end) }}</small>
                  </td>
                  <td class="hr-text-center">
                    <span class="hr-badge hr-badge-primary">{{ week.days_worked }}</span>
                  </td>
                  <td class="hr-text-center">
                    <strong>{{ parseFloat(week.total_hours).toFixed(1) }}h</strong>
                  </td>
                  <td class="hr-text-center">
                    <span v-if="week.late_count > 0" class="hr-badge hr-badge-warning">{{ week.late_count }}</span>
                    <span v-else class="hr-text-muted">-</span>
                  </td>
                  <td class="hr-text-center">
                    {{ (parseFloat(week.total_hours) / parseInt(week.days_worked)).toFixed(1) }}h
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-error-circle hr-me-2 hr-text-warning"></i>Top Late Days</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="summaryData.topLateDays && summaryData.topLateDays.length > 0">
              <div v-for="(day, index) in summaryData.topLateDays" :key="index" class="hr-flex hr-align-center hr-mb-3 hr-pb-3 hr-border-bottom">
                <div class="hr-avatar-xs hr-bg-warning hr-me-3">{{ index + 1 }}</div>
                <div class="hr-flex-1">
                  <h6 class="hr-mb-1">{{ formatDate(day.date) }}</h6>
                  <p class="hr-text-muted hr-mb-0 hr-small">{{ day.day_name }} · Arrived: {{ day.first_in }}</p>
                </div>
                <span class="hr-badge hr-badge-warning">{{ day.late_minutes }} min</span>
              </div>
            </div>
            <div v-else class="hr-text-center hr-text-muted hr-py-4">
              <i class="bx bx-check-circle bx-lg hr-text-success"></i>
              <p class="hr-mt-2 hr-mb-0">Great! No late arrivals</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Activity -->
      <div class="hr-chart-card">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title"><i class="bx bx-history hr-me-2"></i>Recent Activity</h5>
          <button @click="gotTo('employee-attendance-records')" class="hr-btn hr-btn-sm hr-btn-outline-primary">
            <i class="bx bx-right-arrow-alt hr-me-1"></i>View All Records
          </button>
        </div>
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-sm hr-table-hover">
              <thead>
              <tr>
                <th>Date & Time</th>
                <th>Action</th>
                <th>Location Type</th>
                <th>Location</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(activity, index) in summaryData.recentActivity" :key="index">
                <td>
                  <strong>{{ formatDate(activity.date) }}</strong>
                  <br>
                  <small class="hr-text-muted">{{ activity.time }}</small>
                </td>
                <td>
                  <span :class="activity.direction === 'enter' ? 'hr-badge hr-badge-success' : 'hr-badge hr-badge-danger'">
                    {{ activity.direction === 'enter' ? 'Clock In' : 'Clock Out' }}
                  </span>
                </td>
                <td>
                  <span v-if="activity.location_type" :class="getLocationBadgeClass(activity.location_type)">
                    {{ activity.location_type }}
                  </span>
                  <span v-else class="hr-text-muted">-</span>
                </td>
                <td>
                  <div v-if="activity.location_name || activity.address">
                    <div v-if="activity.location_name"><strong>{{ activity.location_name }}</strong></div>
                    <div v-if="activity.address" class="hr-text-muted hr-small">
                      <i class="bx bx-map-pin hr-me-1"></i>{{ activity.address }}
                    </div>
                  </div>
                  <span v-else class="hr-text-muted">-</span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeAttendanceSummary",
  components: { HrPageLayout },

  data() {
    return {
      loading: false,
      summaryData: null,
      selectedMonth: '',
      maxMonth: ''
    };
  },

  mounted() {
    const now = new Date();
    this.selectedMonth = now.toISOString().slice(0, 7);
    this.maxMonth = this.selectedMonth;
    this.loadSummary();
  },

  methods: {
    async loadSummary() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployeeAttendanceSummary");
        data.append("month", this.selectedMonth);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.summaryData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load summary');
        }
      } catch (error) {
        this.$toast?.error("Failed to load attendance summary: " + error.message) ||
        console.error("Failed to load summary:", error);
      } finally {
        this.loading = false;
      }
    },

    getPerformanceLabel(score) {
      if (score >= 95) return 'Excellent';
      if (score >= 85) return 'Very Good';
      if (score >= 75) return 'Good';
      if (score >= 60) return 'Fair';
      return 'Needs Improvement';
    },

    getLocationBadgeClass(type) {
      const typeMap = {
        'Office': 'hr-badge hr-badge-success',
        'Remote': 'hr-badge hr-badge-info',
        'Outside': 'hr-badge hr-badge-warning'
      };
      return typeMap[type] || 'hr-badge hr-badge-secondary';
    },

    getLocationProgressClass(type) {
      const typeMap = {
        'Office': 'hr-progress-fill hr-bg-success',
        'Remote': 'hr-progress-fill hr-bg-info',
        'Outside': 'hr-progress-fill hr-bg-warning'
      };
      return typeMap[type] || 'hr-progress-fill';
    },

    getLocationPercentage(count) {
      if (!this.summaryData || !this.summaryData.locationBreakdown) return 0;
      const total = this.summaryData.locationBreakdown.reduce((sum, loc) => sum + parseInt(loc.count), 0);
      return total > 0 ? (count / total) * 100 : 0;
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hr-display-3 { font-size: 2.5rem; font-weight: 700; }
.hr-bg-orange { background-color: #fd7e14; color: white; }
.hr-border-x { border-left: 1px solid var(--hr-border, #dee2e6); border-right: 1px solid var(--hr-border, #dee2e6); }
.hr-avatar-sm { width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
.hr-avatar-xs { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: 600; }
.hr-span-2 { grid-column: span 2; }
.hr-align-center { align-items: center; }
.hr-d-block { display: block; }
.hr-ms-2 { margin-left: 8px; }
.hr-fs-sm { font-size: 0.875rem; }
.hr-fs-2 { font-size: 2rem; }

@media (max-width: 768px) {
  .hr-display-3 { font-size: 2rem; }
  .hr-span-2 { grid-column: span 1; }
}
</style>
